import { Column } from "@/components/common/Table";
import { useRef, useEffect, useState } from "react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";

export interface RiskDriver {
  id: number;
  riskDriver: string;
  trend: string;
  trendImage: string;
  color?: string; // "green", "yellow", "amber", or "red"
}

// Component to handle conditional tooltip
const RiskDriverCell: React.FC<{ row: RiskDriver }> = ({ row }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isTextTruncated =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isTextTruncated);
      }
    };

    // Small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkTruncation, 0);

    window.addEventListener("resize", checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [row.riskDriver]);

  const textContent = (
    <Typography
      ref={textRef}
      variant='medium-14'
      className={`truncate whitespace-nowrap max-w-[230px]${
        isTruncated ? "cursor-pointer" : ""
      }`}>
      {row.riskDriver}
    </Typography>
  );

  // Only wrap with tooltip if text is truncated
  if (!isTruncated) {
    return textContent;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className='cursor-pointer'>{textContent}</div>
        </TooltipTrigger>
        <TooltipContent
          side='top'
          align='start'
          className='max-w-xs bg-gray-700 text-grey-100 break-words z-50'>
          <Typography variant='regular-12' className='text-grey-100'>
            {row.riskDriver}
          </Typography>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const columns: Column<RiskDriver>[] = [
  {
    key: "riskDriver",
    label: "Risk Drivers",
    isSortable: false,
    render: (row) => <RiskDriverCell row={row} />,
  },
  {
    key: "trend",
    label: "Trend",
    isSortable: false,
    render: (row) => (
      <div className='flex items-center justify-start'>
        <img
          src={row.trendImage}
          alt={`${row.riskDriver} trend`}
          className='h-[27px] w-auto'
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
          }}
        />
      </div>
    ),
  },
];
